import { head, last } from 'lodash';

import { MAP_LINE_COLOR, MAP_LINE_STYLE_SOLID } from '@/lib/constants';
import {
  LocationInterface,
  MapLocationInterface,
  MapRouteInterface,
  RouteLocationInterface,
  RouteScheduleSegmentInterface,
  RouteSegmentInterface,
} from '@/types';
import { RouteTransferApiType, SegmentModeType } from '@/types/api-types';
import { getPreferredIdentifier } from '@/utils/helpers';
import { getTransportColor } from '@/utils/helpers/get-transport-color';

/**
 * Converts RoutelocationInterface to LocationInterface needed by Map
 * @param location RouteLocationInterface
 * @param transferType RouteTransferApiType (optional)
 * @returns LocationInterface
 */
const transformSegmentLocation = (
  location?: RouteLocationInterface | null,
  transferType?: RouteTransferApiType | null,
): LocationInterface | null => {
  if (!location) return null;
  const { type, name, nodeReference, position, identifiers } = location;
  const identifier = getPreferredIdentifier(identifiers);

  return {
    type,
    from: 'qayaq',
    name,
    shortName: name,
    placeId: nodeReference,
    location: position,
    transferType: transferType || null,
    identifier,
  };
};

/**
 * Converts a Route into a RoutePath Obj
 * @param routeId string
 * @param segments RouteSegmentInterface[]
 * @param showColoredLines boolean
 * @returns MapRouteInterface
 */
const transformMapRoute = <T extends RouteSegmentInterface | RouteScheduleSegmentInterface>(
  routeId: string,
  segments: T[],
  showColoredLines = true,
  primaryMode?: SegmentModeType,
): MapRouteInterface => {
  // Prune transfers
  const filteredSegments = segments.filter((segment) => segment.mode !== 'transfer');
  const onlyTransfers = segments.filter((segment) => segment.mode === 'transfer');

  // Build Path Pairs from Segments
  const paths = filteredSegments.reduce((acc, segment) => {
    const { from, to, id: segmentId, mode, key, pathIds, stopWayPoints } = segment;
    const transformedStopLocations = stopWayPoints?.length ? stopWayPoints.map((s) => transformSegmentLocation(s)) : [];
    const lineColor = showColoredLines ? getTransportColor(mode, true) : MAP_LINE_COLOR;

    const segmentLocations: LocationInterface[] = [
      transformSegmentLocation(from),
      ...transformedStopLocations,
      transformSegmentLocation(to),
    ].filter(Boolean) as LocationInterface[];

    // When generating a polyline we need to ensure that  the line starts and
    // ends at the port. To do this we generate an array of location pairs
    // which represents the start and end of each leg of a segment,
    // ie [[port A, Stop A], [Stop A, Port B], [Port B,]]
    const locationPairs = segmentLocations.reduce((locationAcc, location, i) => {
      if (i < segmentLocations.length - 1) {
        return [...locationAcc, [location, segmentLocations[i + 1]]];
      }
      return locationAcc;
    }, [] as LocationInterface[][]);

    const mapLocations: MapLocationInterface[] = locationPairs.map((locationPair, i) => ({
      id: `${i}-${segmentId}`,
      mode,
      key: pathIds?.[i] || key,
      color: lineColor,
      style: MAP_LINE_STYLE_SOLID,
      locations: locationPair,
    }));

    return [...acc, ...mapLocations];
  }, [] as MapLocationInterface[]);

  // Build Markers Array including transfer type data
  const markers = onlyTransfers.map((segment) => {
    const { from, transferType } = segment;
    return transformSegmentLocation(from, transferType);
  });

  // add on first and last marker
  const origLocation = transformSegmentLocation(head(segments)?.from);
  const destLocation = transformSegmentLocation(last(segments)?.to);

  return {
    id: routeId,
    primaryMode,
    paths,
    markers: [origLocation, ...markers, destLocation].filter(Boolean) as LocationInterface[],
  };
};

export { transformMapRoute, transformSegmentLocation };
